import { environment } from '@/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable } from 'rxjs';
import { handleError } from '../tools/handle-error.tools';
import { Edicion } from '@/app/models/interface/edicion.model';
import {
  PaginatorModel,
  HasPagination,
} from '@/app/models/interface/paginator.model';
import {
  Programa,
  ProgramaEstado,
} from '@/app/models/interface/programa.model';
import { CustomResponse } from '@/app/models/interface/response.model';

@Injectable({ providedIn: 'root' })
export class ProgramaService {
  private readonly http = inject(HttpClient);
  private readonly url = environment.api + 'programa/';

  findProgramaEdicion(id: string) {
    const url = this.url + 'programa-edicion/' + id;
    return this.http.get<CustomResponse<Programa>>(url).pipe(
      catchError((err) => handleError(err)),
      map((data) => data.result),
    );
  }

  findEdiciones(id: string) {
    const url = this.url + 'ediciones/' + id;
    return this.http.get<CustomResponse<Edicion[]>>(url).pipe(
      catchError((err) => handleError(err)),
      map((data) => data.result),
    );
  }

  create(data: Partial<Programa>) {
    return this.http.post<CustomResponse<Programa>>(this.url, data).pipe(
      catchError((err) => handleError(err)),
      map((data) => data.result),
    );
  }

  /**
   * @description Requested params
   * @param params: {
   * activo: boolean;
   * id_visible: number;
   * nombre_usuario_aprobador: string;
   * id_usuario: number;
   * nombre_usuario: string;
   * withRelations: boolean;
   * }
   */
  findAll(
    params?: HttpParams,
  ): Observable<PaginatorModel<Programa[], HasPagination>> {
    return this.http
      .get<CustomResponse<PaginatorModel<Programa[], HasPagination>>>(
        this.url,
        {
          params,
        },
      )
      .pipe(
        catchError((err) => handleError(err)),
        map(({ result }) => ({
          metadata: result.metadata,
          data: result.data.map((programa) => ({
            ...programa,
            tematicas: programa.tematicas.map((tematica) => ({
              ...tematica,
              programasItems: tematica.programasItems.map((item) => ({
                ...item,
                capacitacion: {
                  ...item.capacitacion,
                  actividades:
                    item.capacitacion.actividadCompetenciaCapacitaciones?.map(
                      (acc) => acc.actividad,
                    ) || [],
                  areas:
                    item.capacitacion.areaCapacitaciones?.map(
                      (areaCapacitacion) => ({
                        ...areaCapacitacion.area,
                        tiempoEnMin: areaCapacitacion.tiempoEnMin,
                      }),
                    ) || [],
                },
                actividades:
                  item.programasActividades?.map(
                    ({ actividad }) => actividad,
                  ) || [],
                competencias:
                  item.programasCompetencias?.map(
                    ({ competencia }) => competencia,
                  ) || [],
              })),
            })),
          })),
        })),
      );
  }

  findOne(id: string) {
    const url = this.url + id;
    return this.http.get<CustomResponse<Programa>>(url).pipe(
      catchError((err) => handleError(err)),
      map(({ result }) => ({
        ...result,
        tematicas: result.tematicas.map((tematica) => ({
          ...tematica,
          programasItems: tematica.programasItems.map((item) => ({
            ...item,
            capacitacion: {
              ...item.capacitacion,
              actividades:
                item.capacitacion.actividadCompetenciaCapacitaciones?.map(
                  (acc) => acc.actividad,
                ) || [],
              areas:
                item.capacitacion.areaCapacitaciones?.map(
                  (areaCapacitacion) => ({
                    ...areaCapacitacion.area,
                    tiempoEnMin: areaCapacitacion.tiempoEnMin,
                  }),
                ) || [],
            },
            actividades:
              item.programasActividades?.map(({ actividad }) => actividad) ||
              [],
            competencias:
              item.programasCompetencias?.map(
                ({ competencia }) => competencia,
              ) || [],
          })),
        })),
      })),
    );
  }

  update(id: string, data: Partial<Programa>) {
    const dir = this.url + id;
    return this.http.put<CustomResponse<Programa>>(dir, data).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        const programa = data.result;
        return {
          ...programa,
          tematicas:
            programa.tematicas?.map((tematica) => ({
              ...tematica,
              programasItems:
                tematica.programasItems?.map((item) => ({
                  ...item,
                  capacitacion: {
                    ...item.capacitacion,
                    actividades:
                      item.capacitacion?.actividadCompetenciaCapacitaciones?.map(
                        (acc) => acc.actividad,
                      ) || [],
                  },
                  actividades:
                    item.programasActividades?.map(
                      ({ actividad }) => actividad,
                    ) || [],
                  competencias:
                    item.programasCompetencias?.map(
                      ({ competencia }) => competencia,
                    ) || [],
                })) || [],
            })) || [],
        };
      }),
    );
  }

  approveEdicion(id: string) {
    const dir = this.url + 'approveEdicion/' + id;
    return this.http.put<CustomResponse<Programa>>(dir, {}).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        return data.result;
      }),
    );
  }

  rejectEdicion(id: string) {
    const dir = this.url + 'rejectEdicion/' + id;
    return this.http.put<CustomResponse<Programa>>(dir, {}).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        return data.result;
      }),
    );
  }

  changeState(id: string, data: { comentario: string; state: ProgramaEstado }) {
    const dir = this.url + id;
    return this.http.patch<CustomResponse<Programa>>(dir, data).pipe(
      catchError((err) => handleError(err)),
      map((data) => data.result),
    );
  }

  findAllEdiciones(
    params?: HttpParams,
  ): Observable<PaginatorModel<Programa[], HasPagination>> {
    const dir = this.url + 'ediciones/findAll';
    return this.http
      .get<CustomResponse<PaginatorModel<Programa[], HasPagination>>>(dir, {
        params,
      })
      .pipe(
        catchError((err) => handleError(err)),
        map(({ result }) => ({
          metadata: result.metadata,
          data: result.data.map((programa) => ({
            ...programa,
            tematicas: programa.tematicas.map((tematica) => ({
              ...tematica,
              programasItems: tematica.programasItems.map((item) => ({
                ...item,
                capacitacion: {
                  ...item.capacitacion,
                  actividades:
                    item.capacitacion.actividadCompetenciaCapacitaciones?.map(
                      (acc) => acc.actividad,
                    ) || [],
                  areas:
                    item.capacitacion.areaCapacitaciones?.map(
                      (areaCapacitacion) => ({
                        ...areaCapacitacion.area,
                        tiempoEnMin: areaCapacitacion.tiempoEnMin,
                      }),
                    ) || [],
                },
                actividades:
                  item.programasActividades?.map(
                    ({ actividad }) => actividad,
                  ) || [],
                competencias:
                  item.programasCompetencias?.map(
                    ({ competencia }) => competencia,
                  ) || [],
              })),
            })),
          })),
        })),
      );
  }
}
